/**
 * WhatsAppService
 * Service for WhatsApp messaging operations
 * Route: api/whatsapp
 */
import { BaseService } from '../base/BaseService';
/**
 * WhatsAppService
 * Handles WhatsApp message sending and management operations
 */
export class WhatsAppService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? WhatsAppService.DEFAULT_SERVICE_PATH,
            basePath: WhatsAppService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== MESSAGE OPERATIONS ====================
    /**
     * Get all WhatsApp messages with caching
     * GET api/whatsapp/messages
     */
    async getMessages() {
        const response = await this.getMessagesRaw();
        return response.data ?? [];
    }
    /**
     * Get messages with full response
     */
    getMessagesRaw() {
        const endpoint = this.getEndpoint('messages', 'messages');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get message by ID
     * GET api/whatsapp/messages/{id}
     */
    async getMessageById(id) {
        const response = await this.getMessageByIdRaw(id);
        if (!response.data) {
            throw new Error(`WhatsApp message ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get message by ID with full response
     */
    getMessageByIdRaw(id) {
        const endpoint = this.getEndpoint('message', `messages/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Create a new WhatsApp message
     * POST api/whatsapp/messages
     */
    async createMessage(message) {
        const response = await this.createMessageRaw(message);
        if (!response.data) {
            throw new Error(response.message || 'Failed to create WhatsApp message');
        }
        return response.data;
    }
    /**
     * Create message with full response
     */
    createMessageRaw(message) {
        const endpoint = this.getEndpoint('messages', 'messages');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, message);
    }
    /**
     * Update an existing WhatsApp message
     * PUT api/whatsapp/messages/{id}
     */
    async updateMessage(id, message) {
        const response = await this.updateMessageRaw(id, message);
        if (!response.data) {
            throw new Error(response.message || 'Failed to update WhatsApp message');
        }
        return response.data;
    }
    /**
     * Update message with full response
     */
    updateMessageRaw(id, message) {
        const endpoint = this.getEndpoint('message-update', `messages/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.put(url, message);
    }
    /**
     * Delete a WhatsApp message
     * DELETE api/whatsapp/messages/{id}
     */
    async deleteMessage(id) {
        const response = await this.deleteMessageRaw(id);
        return response.success;
    }
    /**
     * Delete message with full response
     */
    deleteMessageRaw(id) {
        const endpoint = this.getEndpoint('message-delete', `messages/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.delete(url);
    }
    /**
     * Get all WhatsApp messages as model views
     * GET api/whatsapp/messages/view
     */
    async getMessageViews() {
        const response = await this.getMessageViewsRaw();
        return response.data ?? [];
    }
    /**
     * Get message views with full response
     */
    getMessageViewsRaw() {
        const endpoint = this.getEndpoint('messages-view', 'messages/view');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get WhatsApp message model view by ID
     * GET api/whatsapp/messages/view/{id}
     */
    async getMessageViewById(id) {
        const response = await this.getMessageViewByIdRaw(id);
        if (!response.data) {
            throw new Error(`WhatsApp message ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get message view by ID with full response
     */
    getMessageViewByIdRaw(id) {
        const endpoint = this.getEndpoint('message-view', `messages/view/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== TEMPLATE PARAMETER OPERATIONS ====================
    /**
     * Get template parameters by template ID
     * GET api/whatsapp/templates/{templateId}/parameters
     */
    async getTemplateParameters(templateId) {
        const response = await this.getTemplateParametersRaw(templateId);
        return response.data ?? [];
    }
    /**
     * Get template parameters with full response
     */
    getTemplateParametersRaw(templateId) {
        const endpoint = this.getEndpoint('template-params', `templates/${templateId}/parameters`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Create a new template parameter
     * POST api/whatsapp/templates/{templateId}/parameters
     */
    async createTemplateParameter(templateId, parameter) {
        const response = await this.createTemplateParameterRaw(templateId, parameter);
        if (!response.data) {
            throw new Error(response.message || 'Failed to create template parameter');
        }
        return response.data;
    }
    /**
     * Create template parameter with full response
     */
    createTemplateParameterRaw(templateId, parameter) {
        const endpoint = this.getEndpoint('template-param-create', `templates/${templateId}/parameters`);
        const url = this.buildUrl(endpoint);
        return this.http.post(url, parameter);
    }
    /**
     * Delete a template parameter
     * DELETE api/whatsapp/templates/parameters/{parameterId}
     */
    async deleteTemplateParameter(parameterId) {
        const response = await this.deleteTemplateParameterRaw(parameterId);
        return response.success;
    }
    /**
     * Delete template parameter with full response
     */
    deleteTemplateParameterRaw(parameterId) {
        const endpoint = this.getEndpoint('template-param-delete', `templates/parameters/${parameterId}`);
        const url = this.buildUrl(endpoint);
        return this.http.delete(url);
    }
    // ==================== 360DIALOG SEND OPERATIONS ====================
    /**
     * Send a text message via 360dialog WhatsApp API
     * POST api/whatsapp/send/text
     */
    async sendText(request) {
        const response = await this.sendTextRaw(request);
        if (!response.data) {
            throw new Error(response.message || 'Failed to send text message');
        }
        return response.data;
    }
    /**
     * Send text message with full response
     */
    sendTextRaw(request) {
        const endpoint = this.getEndpoint('send-text', 'send/text');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, request);
    }
    /**
     * Send a template message via 360dialog WhatsApp API
     * POST api/whatsapp/send/template
     */
    async sendTemplate(request) {
        const response = await this.sendTemplateRaw(request);
        if (!response.data) {
            throw new Error(response.message || 'Failed to send template message');
        }
        return response.data;
    }
    /**
     * Send template message with full response
     */
    sendTemplateRaw(request) {
        const endpoint = this.getEndpoint('send-template', 'send/template');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, request);
    }
    /**
     * Send an image message via 360dialog WhatsApp API
     * POST api/whatsapp/send/image
     */
    async sendImage(request) {
        const response = await this.sendImageRaw(request);
        if (!response.data) {
            throw new Error(response.message || 'Failed to send image message');
        }
        return response.data;
    }
    /**
     * Send image message with full response
     */
    sendImageRaw(request) {
        const endpoint = this.getEndpoint('send-image', 'send/image');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, request);
    }
    /**
     * Send a document message via 360dialog WhatsApp API
     * POST api/whatsapp/send/document
     */
    async sendDocument(request) {
        const response = await this.sendDocumentRaw(request);
        if (!response.data) {
            throw new Error(response.message || 'Failed to send document message');
        }
        return response.data;
    }
    /**
     * Send document message with full response
     */
    sendDocumentRaw(request) {
        const endpoint = this.getEndpoint('send-document', 'send/document');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, request);
    }
    /**
     * Get message status from 360dialog API
     * GET api/whatsapp/status/{whatsAppMessageId}
     */
    async getMessageStatus(whatsAppMessageId) {
        const response = await this.getMessageStatusRaw(whatsAppMessageId);
        if (!response.data) {
            throw new Error(response.message || 'Failed to get message status');
        }
        return response.data;
    }
    /**
     * Get message status with full response
     */
    getMessageStatusRaw(whatsAppMessageId) {
        const endpoint = this.getEndpoint('status', `status/${whatsAppMessageId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== CACHE OPERATIONS ====================
    /**
     * Invalidate WhatsApp cache
     * POST api/whatsapp/cache/invalidate
     */
    async invalidateCache() {
        const response = await this.invalidateCacheRaw();
        return response.success;
    }
    /**
     * Invalidate cache with full response
     */
    invalidateCacheRaw() {
        const endpoint = this.getEndpoint('cache-invalidate', 'cache/invalidate');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, {});
    }
}
WhatsAppService.DEFAULT_SERVICE_PATH = 'communications';
WhatsAppService.BASE_PATH = 'api/whatsapp';
//# sourceMappingURL=WhatsAppService.js.map